import { type onClusterClickHandler,defaultOnClusterClickHandler, MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';

import { GoogleMapsLibrary } from '~/framework/helpers/google/google-maps-library';

import { initializeIcons } from './gmaps-markers';
import CarIcon from "./icon-car.svg";
import { type Icons, type LatLng, type MarkerClickFunction, type MarkerData } from './interfaces';


export class GmapsMapController {
    static async mountMap(mapDiv: HTMLElement, options?: google.maps.MapOptions) {
        const { Map: map } = await GoogleMapsLibrary.mapsLibrary();
        const icons = await initializeIcons();
        return new GmapsMapController(new map(mapDiv, options), icons);
    }
    
    private readonly _map: google.maps.Map;
    private _markers : Array<{marker: google.maps.Marker, markerData: MarkerData}>;
    private _markerClickHandler? : MarkerClickFunction;
    private _markerClustererClickHandler : onClusterClickHandler;
    private _routeMarker? : google.maps.Marker;
    private readonly _icons : Icons;
    private readonly _infoWindow : google.maps.InfoWindow;
    private readonly _directionsService : google.maps.DirectionsService;
    private readonly _directionsRenderer: google.maps.DirectionsRenderer;
    private readonly _markerClusterer : MarkerClusterer;
    
    constructor(map: google.maps.Map, icons: Icons) {
        this._map = map;
        this._markers = [];
        this._icons = icons;
        this._infoWindow = new google.maps.InfoWindow({
            zIndex: 10,
        });

        // Check if 
        const appEl = document.getElementById('app');
        const momentaTheme = appEl && appEl.dataset.theme === 'momenta';

        this._routeMarker = undefined;
        this._directionsService = new google.maps.DirectionsService();
        this._directionsRenderer = new google.maps.DirectionsRenderer({
            suppressMarkers: true,
            polylineOptions: {
                strokeWeight: 6,
                strokeColor: momentaTheme ? '#FF5200' : '#0071fd',
                zIndex: 9,
            }
        });
        this._markerClustererClickHandler = defaultOnClusterClickHandler;
        this._markerClickHandler = undefined;
        this._markerClusterer = new MarkerClusterer({
            map: this._map,
            algorithm: new SuperClusterAlgorithm({
                radius: 80,
                maxZoom: 12
            }),
            onClusterClick: (event, cluster, map) => (this._markerClustererClickHandler?.(event, cluster, map)),
            renderer: { render: ({ count, position }: {count: number, position:google.maps.LatLng}) => {
                return new google.maps.Marker({
                    position,
                    icon: this._icons.clusterPin,
                    label: {
                        text: String(count),
                        color: "white",
                        fontSize: "24px",
                        fontWeight: '500',
                        fontFamily: 'Work Sans'
                    },
                    // adjust zIndex to be above other markers
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                });
            }}
        });
        this._directionsRenderer.setMap(this._map);
    }
    
    addMarker(marker: google.maps.Marker, markerData: MarkerData) {
        marker.setMap(this._map);
        this._markers.push({marker, markerData});
        this._markerClusterer.addMarker(marker, true);
    }

    clearMarkers() {
        
        if (this._markers.length > 0) {
            this._markerClusterer.clearMarkers();
        }
        
        for (const marker of this._markers) {
            marker.marker.setMap(null);
            marker.marker.setVisible(false);
        }

        this._markers = [];
    }

    setActiveIcon(marker: google.maps.Marker, icon: string) {
        marker.setIcon(this._icons[icon] ?? this._icons.building)
    }

    // This is used by LLC to highlight selected icon
    setAllIcons(icon: string) {
        for (const marker of this._markers) {
            // Issue #168108
            if (!marker.markerData?.assetData?.type || marker.markerData?.assetData.type !== 'communityMarker') {
                marker.marker.setIcon(this._icons[icon]);
            }
        }
    }

    setClusterClickHandler(f: onClusterClickHandler) {
        this._markerClustererClickHandler = f;
    }
    
    setMarkerClickHandler(f: MarkerClickFunction) {
        this._markerClickHandler = f;
    }

    setMapCenter(center: LatLng) {
        this._map.setCenter(center);
    }

    panTo(center: LatLng) {
        this._map.panTo(center);
    }

    setMapZoom(zoom: number) {
        this._map.setZoom(zoom);
    }
    
    smoothZoom(max: number, cnt: number) {
        if (cnt >= max) {
            return;
        }
        else {
            google.maps.event.addListenerOnce(this._map, 'zoom_changed', () => {
                this.smoothZoom(max, cnt + 1);
            });
            setTimeout(() => {this._map.setZoom(cnt)}, 80);
        }  
    }


    setMarkersFromMarkerData(markerData: MarkerData[], fitBounds = true): void {
        this.clearMarkers();
        for (const m of markerData) {
            const marker = new google.maps.Marker({
                position: m.location,
                icon: this._icons[m.markerImage] ?? this._icons.building,
                zIndex: m.zIndex ?? 7,
                title: m.title ?? undefined,
            });
            if (m.label) {
                marker.setLabel({
                    className: 'map-marker-label',
                    text: m.label,
                    color: "black",
                    fontSize: "12px",
                    fontWeight: '500',
                    fontFamily: 'Work Sans'
                })
            }

            marker.addListener("click", () => {
                this._markerClickHandler?.(marker, m);
            });

            this.addMarker(marker, m);
        }

        if (fitBounds) {
            this.fitBoundsToMarkers();
        }
    }

    async route(origin: LatLng, destination: LatLng, markerImage: string) {
        const directions = await this._directionsService.route({
            origin: origin,
            destination: destination,
            // @ts-expect-error Google Namespace is not available in this file
            travelMode: "DRIVING"
        });
        this._routeMarker?.setMap(null);
        this._routeMarker?.setVisible(false);
        this._routeMarker = new google.maps.Marker({
            position: destination,
            icon: this._icons[markerImage] ?? this._icons.building,
            map: this._map,
        });
        const firstRoute = directions.routes[0] ?? false;
        if(firstRoute?.legs) {
            const halfwayPoint = firstRoute.overview_path[Math.floor(firstRoute.overview_path.length / 2)];
            if(firstRoute.legs[0]?.distance && firstRoute.legs[0].duration) {
                this._infoWindow.close();
                const content = `
                    <div class="gm-style-iw-container">
                        <div>
                            <img src="${CarIcon}" />
                        </div>
                        <div>
                            <div class="gm-style-iw-time">${firstRoute.legs[0].duration.text} </div>
                            <div class="gm-style-iw-distance">${firstRoute.legs[0].distance.text}</div>
                        </div>
                    </div>
                `
                this._infoWindow.setContent(content);
                this._infoWindow.setPosition(halfwayPoint);
                this._infoWindow.open(this.map);
            }

            this._directionsRenderer.setDirections(directions);
        }
    }

    clearRoute() {
        this._directionsRenderer.setDirections(null);
        this._routeMarker?.setMap(null);
    }

    fitBounds(points: LatLng[]) {
        const bounds = new google.maps.LatLngBounds();
        for(const p of points) {
            bounds.extend(p);
        }

        this._map.fitBounds(bounds);
    }

    fitBoundsToMarkers() {
        const bounds = new google.maps.LatLngBounds();
        for(const marker of this._markers) {
            const position = marker.marker.getPosition();
            if(position) {
                bounds.extend(position);
            }
        }
        
        if (!bounds.isEmpty()) {
            this._map.fitBounds(bounds);
            // Set minimum zoom to be 8
            google.maps.event.addListenerOnce(this._map, "bounds_changed", () => {
                if(this._map.getZoom()) {
                    this._map.setZoom(Math.min(this._map.getZoom() ?? 16, 16));
                }
            });
        }
    }

    resetMarkerIcons() {
        for(const marker of this._markers) {
            marker.marker.setIcon(this._icons[marker.markerData.markerImage] ?? this._icons.building);
        }
    }

    set clusteringEnabled(clusteringEnabled: boolean) {
        this._markerClusterer.setMap(clusteringEnabled ? this._map : null);
    }
    
    get map() {
        return this._map;
    }
}